"use client";

import React, { useState, useRef, useEffect, useMemo } from "react";
import { Plus, Edit, Trash2, CheckSquare, X, Search, ChevronUp, ChevronDown } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "./Masters/Button";
import RTE from "./RTE";
import {
  useGetTourInclusionDetails,
  useAddTourInclusionDetails,
  useUpdateTourInclusionDetails,
  useTourInclusionStatus,
  useDeleteTourInclusions,
  useGetCountries,
  useTourBookingType,
} from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";
import { toast } from "react-toastify";

// Zod Schema for Tour Inclusion Form
const tourInclusionSchema = z.object({
  country_id: z.coerce.number().min(1, "Country is required"),
  booking_type: z.coerce.number().min(1, "Booking Type is required"),
  title: z.string().min(1, "Tours Inclusion Title is required"),
  description: z.string().min(1, "Content is required"),
  thumbImage: z.any().optional(), // Optional for now as backend doesn't have this field
});

type TourInclusionFormData = z.infer<typeof tourInclusionSchema>;

interface TourInclusionRecord {
  id: number;
  country_id: number;
  booking_type: number;
  title: string;
  description: string;
  status: number;
  created_by: number;
  modified_by: number | null;
  created_date: string;
  modified_date: string | null;
  ip: string | null;
  country_name?: string;
  package_name?: string;
  created_by_name?: string;
}

interface Country {
  id: number;
  name: string;
}

interface BookingType {
  id: number;
  tour_type: string;
}

const TourInclusions: React.FC = () => {
  const { user } = useAuth();
  const [showFormSection, setShowFormSection] = useState(false);
  const [editingRecord, setEditingRecord] = useState<TourInclusionRecord | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [pageSize, setPageSize] = useState(10);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // API Hooks
  const { data: inclusionData, isLoading: isLoadingInclusions } = useGetTourInclusionDetails();
  const { mutate: addTourInclusion, isPending: isAdding } = useAddTourInclusionDetails();
  const { mutate: updateTourInclusion, isPending: isUpdating } = useUpdateTourInclusionDetails();
  const { mutate: updateStatus, isPending: isUpdatingStatus } = useTourInclusionStatus();
  const { mutate: deleteTourInclusions, isPending: isDeleting } = useDeleteTourInclusions();
  const { data: countries = [], isLoading: isLoadingCountries } = useGetCountries();
  const bookingTypesQuery = useTourBookingType();

  // Extract inclusions from API response
  const inclusions: TourInclusionRecord[] = useMemo(() => {
    if (!inclusionData) return [];
    
    if (inclusionData.status === "success" && Array.isArray(inclusionData.data)) {
      return inclusionData.data;
    }
    
    if (Array.isArray(inclusionData)) {
      return inclusionData;
    }
    
    return [];
  }, [inclusionData]);

  // Extract booking types from API response - match working component pattern exactly
  const bookingTypes: BookingType[] = bookingTypesQuery.data?.data || [];

  // Form setup
  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TourInclusionFormData>({
    resolver: zodResolver(tourInclusionSchema),
    defaultValues: {
      country_id: undefined,
      booking_type: undefined,
      title: "",
      description: "",
      thumbImage: undefined,
    },
  });

  const watchedDescription = watch("description");

  // Filter records based on search term
  const filteredRecords = useMemo(() => {
    if (!searchTerm.trim()) return inclusions;
    
    const searchLower = searchTerm.toLowerCase();
    return inclusions.filter((record) => {
      return (
        record.title?.toLowerCase().includes(searchLower) ||
        record.country_name?.toLowerCase().includes(searchLower) ||
        record.package_name?.toLowerCase().includes(searchLower) ||
        record.created_by_name?.toLowerCase().includes(searchLower)
      );
    });
  }, [inclusions, searchTerm]);

  // Paginated records
  const paginatedRecords = useMemo(() => {
    const start = 0;
    const end = pageSize;
    return filteredRecords.slice(start, end);
  }, [filteredRecords, pageSize]);

  // Populate form when editing
  useEffect(() => {
    if (editingRecord) {
      setValue("country_id", editingRecord.country_id);
      setValue("booking_type", editingRecord.booking_type);
      setValue("title", editingRecord.title);
      setValue("description", editingRecord.description || "");
      setShowFormSection(true);
    }
  }, [editingRecord, setValue]);

  // Cost Includes/Excludes checkboxes
  const inclusionItems = [
    "Tax Included",
    "Boat Transfers",
    "Full Board Meals (Breakfast, Lunch, Dinner)",
    "Children allowed (all ages)",
    "Airport Transfers",
    "Eco-Tour Fee",
    "Tour guides - multilingual",
  ];

  const [costIncludes, setCostIncludes] = useState<Record<string, boolean>>(
    inclusionItems.reduce((acc, item) => ({ ...acc, [item]: true }), {})
  );

  const [costExcludes, setCostExcludes] = useState<Record<string, boolean>>(
    inclusionItems.reduce((acc, item) => ({ ...acc, [item]: true }), {})
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setValue("thumbImage", file);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows(paginatedRecords.map((r) => r.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id: number) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const handleCostIncludeChange = (item: string) => {
    setCostIncludes((prev) => ({ ...prev, [item]: !prev[item] }));
  };

  const handleCostExcludeChange = (item: string) => {
    setCostExcludes((prev) => ({ ...prev, [item]: !prev[item] }));
  };

  const onSubmit = (data: TourInclusionFormData) => {
    if (!user?.id) {
      toast.error("User not logged in. Please login again.");
      return;
    }

    const payload: any = {
      country_id: data.country_id,
      booking_type: data.booking_type,
      title: data.title.trim(),
      description: data.description,
      status: 1,
      ip: "127.0.0.1",
      created_by: user.id,
    };

    if (editingRecord) {
      // Update existing record
      updateTourInclusion(
        {
          ...payload,
          auto_id: editingRecord.id,
          modified_by: user.id,
        },
        {
          onSuccess: (response) => {
            if (response?.status === "success") {
              toast.success("Tour inclusion updated successfully!");
              handleCancel();
            } else {
              toast.error(response?.message || "Failed to update tour inclusion");
            }
          },
          onError: (error: any) => {
            console.error("Error updating tour inclusion:", error);
            toast.error(error?.response?.data?.message || "Failed to update tour inclusion");
          },
        }
      );
    } else {
      // Add new record
      addTourInclusion(payload, {
        onSuccess: (response) => {
          if (response?.status === "success") {
            toast.success("Tour inclusion added successfully!");
            handleCancel();
          } else {
            toast.error(response?.message || "Failed to add tour inclusion");
          }
        },
        onError: (error: any) => {
          console.error("Error adding tour inclusion:", error);
          toast.error(error?.response?.data?.message || "Failed to add tour inclusion");
        },
      });
    }
  };

  const handleAddNew = () => {
    setEditingRecord(null);
    reset();
    setShowFormSection(true);
  };

  const handleCancel = () => {
    setShowFormSection(false);
    setEditingRecord(null);
    reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePublish = () => {
    if (selectedRows.length === 0) {
      toast.warn("Please select at least one record to publish");
      return;
    }

    if (!user?.id) {
      toast.error("User not logged in. Please login again.");
      return;
    }

    updateStatus(
      {
        id: selectedRows.join(","),
        user_id: user.id,
        status: 1,
      },
      {
        onSuccess: (response) => {
          if (response?.status === "success") {
            toast.success("Tour inclusions published successfully!");
            setSelectedRows([]);
          } else {
            toast.error(response?.message || "Failed to publish tour inclusions");
          }
        },
        onError: (error: any) => {
          console.error("Error publishing tour inclusions:", error);
          toast.error(error?.response?.data?.message || "Failed to publish tour inclusions");
        },
      }
    );
  };

  const handleUnpublish = () => {
    if (selectedRows.length === 0) {
      toast.warn("Please select at least one record to unpublish");
      return;
    }

    if (!user?.id) {
      toast.error("User not logged in. Please login again.");
      return;
    }

    updateStatus(
      {
        id: selectedRows.join(","),
        user_id: user.id,
        status: 0,
      },
      {
        onSuccess: (response) => {
          if (response?.status === "success") {
            toast.success("Tour inclusions unpublished successfully!");
            setSelectedRows([]);
          } else {
            toast.error(response?.message || "Failed to unpublish tour inclusions");
          }
        },
        onError: (error: any) => {
          console.error("Error unpublishing tour inclusions:", error);
          toast.error(error?.response?.data?.message || "Failed to unpublish tour inclusions");
        },
      }
    );
  };

  const handleDelete = () => {
    if (selectedRows.length === 0) {
      toast.warn("Please select at least one record to delete");
      return;
    }

    if (!user?.id) {
      toast.error("User not logged in. Please login again.");
      return;
    }

    if (!confirm(`Are you sure you want to delete ${selectedRows.length} selected record(s)?`)) {
      return;
    }

    deleteTourInclusions(
      {
        id: selectedRows.join(","),
        user_id: user.id,
      },
      {
        onSuccess: (response) => {
          if (response?.status === "success") {
            toast.success("Tour inclusions deleted successfully!");
            setSelectedRows([]);
          } else {
            toast.error(response?.message || "Failed to delete tour inclusions");
          }
        },
        onError: (error: any) => {
          console.error("Error deleting tour inclusions:", error);
          toast.error(error?.response?.data?.message || "Failed to delete tour inclusions");
        },
      }
    );
  };

  const handleEdit = (record: TourInclusionRecord) => {
    setEditingRecord(record);
  };

  return (
    <div className="space-y-6">
      {/* Tours Inclusions Information Form - Show only when Add New is clicked */}
      {showFormSection && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-800">
              {editingRecord ? "Update Tours Inclusions Information" : "Tours Inclusions Information"}
            </h2>
            <button
              type="button"
              onClick={handleCancel}
              className="text-gray-500 hover:text-gray-700 text-xs"
            >
              Cancel
            </button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Country *
                </label>
                <select
                  {...register("country_id")}
                  className={`w-full border rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                    errors.country_id ? "border-red-500" : "border-gray-300"
                  }`}
                  disabled={isLoadingCountries}
                >
                  <option value="">-- Select Country --</option>
                  {Array.isArray(countries) &&
                    countries.map((country: Country) => (
                      <option key={country.id} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                </select>
                {errors.country_id && (
                  <span className="text-red-500 text-[10px] mt-1 block">
                    {errors.country_id.message}
                  </span>
                )}
              </div>

              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Booking Type *
                </label>
                <select
                  {...register("booking_type")}
                  className={`w-full border rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                    errors.booking_type ? "border-red-500" : "border-gray-300"
                  }`}
                  disabled={bookingTypesQuery.isLoading}
                >
                  <option value="">-- Select Booking Type --</option>
                  {bookingTypesQuery.isLoading ? (
                    <option value="" disabled>Loading booking types...</option>
                  ) : bookingTypes.length === 0 ? (
                    <option value="" disabled>No booking types available</option>
                  ) : (
                    bookingTypes.map((type: BookingType) => (
                      <option key={type.id} value={type.id}>
                        {type.tour_type}
                      </option>
                    ))
                  )}
                </select>
                {errors.booking_type && (
                  <span className="text-red-500 text-[10px] mt-1 block">
                    {errors.booking_type.message}
                  </span>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Tours Inclusion Title *
                </label>
                <input
                  type="text"
                  {...register("title")}
                  placeholder="Inclusions Name"
                  className={`w-full border rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                    errors.title ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.title && (
                  <span className="text-red-500 text-[10px] mt-1 block">
                    {errors.title.message}
                  </span>
                )}
              </div>

              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Thumb Image
                </label>
                <div className="flex items-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-3 py-1.5 border border-gray-300 rounded text-xs hover:bg-gray-50"
                  >
                    Choose File
                  </button>
                  <span className="text-xs text-gray-500">
                    {watch("thumbImage")?.name || "No file chosen"}
                  </span>
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Content *
                </label>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <RTE
                      content={field.value || ""}
                      onChange={(content) => field.onChange(content)}
                    />
                  )}
                />
                {errors.description && (
                  <span className="text-red-500 text-[10px] mt-1 block">
                    {errors.description.message}
                  </span>
                )}
              </div>

              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={isAdding || isUpdating}
                  className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {isAdding || isUpdating
                    ? editingRecord
                      ? "Updating..."
                      : "Submitting..."
                    : editingRecord
                    ? "Update"
                    : "Submit"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* View/Update Records Table - Show only when Add New is clicked */}
      {showFormSection && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h2 className="text-sm font-semibold text-gray-800 mb-4">
            View/Update Records
          </h2>

          {/* Table Controls */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-700">Show</label>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value={10}>10 entries</option>
                <option value={25}>25 entries</option>
                <option value={50}>50 entries</option>
                <option value={100}>100 entries</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left border-b">
                    <input
                      type="checkbox"
                      checked={
                        paginatedRecords.length > 0 &&
                        selectedRows.length === paginatedRecords.length &&
                        paginatedRecords.every((r) => selectedRows.includes(r.id))
                      }
                      onChange={handleSelectAll}
                      className="h-3 w-3 rounded border-gray-300"
                    />
                  </th>
                  <th className="px-3 py-2 text-left border-b">
                    <div className="flex items-center gap-1">
                      S. No
                      <div className="flex flex-col">
                        <ChevronUp className="w-3 h-3 text-gray-400" />
                        <ChevronDown className="w-3 h-3 text-gray-400" />
                      </div>
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left border-b">
                    <div className="flex items-center gap-1">
                      Country
                      <div className="flex flex-col">
                        <ChevronUp className="w-3 h-3 text-gray-400" />
                        <ChevronDown className="w-3 h-3 text-gray-400" />
                      </div>
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left border-b">
                    <div className="flex items-center gap-1">
                      Booking Type
                      <div className="flex flex-col">
                        <ChevronUp className="w-3 h-3 text-gray-400" />
                        <ChevronDown className="w-3 h-3 text-gray-400" />
                      </div>
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left border-b">
                    <div className="flex items-center gap-1">
                      Inclusion Name
                      <div className="flex flex-col">
                        <ChevronUp className="w-3 h-3 text-gray-400" />
                        <ChevronDown className="w-3 h-3 text-gray-400" />
                      </div>
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left border-b">
                    <div className="flex items-center gap-1">
                      Created By
                      <div className="flex flex-col">
                        <ChevronUp className="w-3 h-3 text-gray-400" />
                        <ChevronDown className="w-3 h-3 text-gray-400" />
                      </div>
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left border-b">
                    <div className="flex items-center gap-1">
                      Status
                      <div className="flex flex-col">
                        <ChevronUp className="w-3 h-3 text-gray-400" />
                        <ChevronDown className="w-3 h-3 text-gray-400" />
                      </div>
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left border-b">Action</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingInclusions ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : paginatedRecords.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-3 py-8 text-center text-gray-500"
                    >
                      No data available in table
                    </td>
                  </tr>
                ) : (
                  paginatedRecords.map((record, index) => (
                    <tr key={record.id} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(record.id)}
                          onChange={() => handleSelectRow(record.id)}
                          className="h-3 w-3 rounded border-gray-300"
                        />
                      </td>
                      <td className="px-3 py-2">{index + 1}</td>
                      <td className="px-3 py-2">{record.country_name || "N/A"}</td>
                      <td className="px-3 py-2">{record.package_name || "N/A"}</td>
                      <td className="px-3 py-2">{record.title}</td>
                      <td className="px-3 py-2">{record.created_by_name || "N/A"}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`px-2 py-1 rounded text-[10px] ${
                            record.status === 1
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {record.status === 1 ? "Published" : "Unpublished"}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(record)}
                          className="text-blue-500 hover:text-blue-700"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 text-xs">
            <div className="text-gray-600">
              Showing {paginatedRecords.length === 0 ? 0 : 1} to {paginatedRecords.length} of{" "}
              {filteredRecords.length} entries
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={paginatedRecords.length === 0}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={paginatedRecords.length === 0}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tour Inclusions Checkbox Section - Always visible */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-800">
            Tour Inclusions
          </h2>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="primary"
              onClick={handleAddNew}
              icon={<Plus className="w-4 h-4" />}
              iconPosition="left"
            >
              + Add New
            </Button>
            <Button
              size="sm"
              variant="success"
              onClick={handlePublish}
              disabled={selectedRows.length === 0 || isUpdatingStatus}
            >
              {isUpdatingStatus ? "Publishing..." : "Publish"}
            </Button>
            <Button
              size="sm"
              variant="warning"
              onClick={handleUnpublish}
              disabled={selectedRows.length === 0 || isUpdatingStatus}
            >
              {isUpdatingStatus ? "Unpublishing..." : "Unpublish"}
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={handleDelete}
              disabled={selectedRows.length === 0 || isDeleting}
              icon={<Trash2 className="w-4 h-4" />}
              iconPosition="left"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Cost Includes */}
          <div>
            <h3 className="text-xs font-semibold text-gray-700 mb-3">
              Cost Includes
            </h3>
            <div className="space-y-2">
              {inclusionItems.map((item) => (
                <label
                  key={`include-${item}`}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={costIncludes[item]}
                    onChange={() => handleCostIncludeChange(item)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-700">{item}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Cost Excludes */}
          <div>
            <h3 className="text-xs font-semibold text-gray-700 mb-3">
              Cost Excludes
            </h3>
            <div className="space-y-2">
              {inclusionItems.map((item) => (
                <label
                  key={`exclude-${item}`}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={costExcludes[item]}
                    onChange={() => handleCostExcludeChange(item)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-700">{item}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TourInclusions;